import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { push } from 'connected-react-router';
import { toastr } from 'react-redux-toastr';

import { addWhdStatement, createWhdStatement, getWhdStatementFromAccount } from 'actions';
import WithholdingStatementUpload from 'components/Common/WithholdingStatementUpload';
import WhdAiProcessingModal from 'components/Modals/WhdAiProcessingModal';
import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import valueOrEmpty from 'common/App/valueOrEmpty';
import StatementTypes from 'common/Forms/StatementTypes';

const AccountWhdStatementPanel = ({ accountId, reloadAccountFunction }) => {
  const dispatch = useDispatch();

  const [createType, setCreateType] = useState('');
  const [uploadType, setUploadType] = useState('');
  const [mode, setMode] = useState(null);
  const [uploadedStatement, setUploadedStatement] = useState(null);
  const [showConfirmCreate, setShowConfirmCreate] = useState(false);
  const [showAiProcessingModal, setShowAiProcessingModal] = useState(false);
  const [aiProcessingFileName, setAiProcessingFileName] = useState('');

  const permissions = useSelector(state => state.auth.permissions);
  const isAddingStatement = useSelector(state => state.forms.isAddingStatement);
  const isCreatingNewWhd = useSelector(state => state.forms.isCreatingNewWhd);
  const existingContainer = useSelector(state => state.forms.accountWithholdingStatement); // first element already reduced elsewhere
  const whdValidationResult = existingContainer?.validationResult;
  const withholdingStatement = existingContainer?.statement;

  const canCreate = AccessControl.hasPermission(permissions, [ActivityPerms.multiTierMgrCreateWhs], true);
  const canUpload = AccessControl.hasPermission(permissions, [ActivityPerms.scannedWhdStmtBoUpload], true);
  const canView = AccessControl.hasPermission(permissions, [ActivityPerms.multiTierMgrViewWhs], true);

  useEffect(() => {
    if (accountId) {
      dispatch(getWhdStatementFromAccount(accountId));
    }
  }, [accountId, dispatch]);

  const onCreateTypeChange = (e) => {
    setCreateType(e.target.value);
    setUploadType('');
    setMode('Create');
    setUploadedStatement(null);
  };

  const onUploadTypeChange = (e, newValue) => {
    setUploadType(newValue);
    setCreateType('');
    setMode('Add');
    setUploadedStatement(null);
  };

  const onFileUploadSuccess = (file, response) => {
    setUploadedStatement(response);
  };

  const onFileUploadError = () => {
    setUploadedStatement(null);
  };

  const onFileUploadCancel = (file) => {
    toastr.info('', `Upload of ${ file.name } canceled`);
    setUploadedStatement(null);
  };

  const onDeleteUploaded = () => {
    setUploadedStatement(null);
  };

  const confirmCreate = () => {
    if (!createType) {
      toastr.warning('', 'Please select a statement type');
      return;
    }
    setShowConfirmCreate(false);
    dispatch(createWhdStatement(0, accountId, createType, reloadAccountFunction));
  };

  const openCreateConfirm = () => {
    if (!createType) {
      toastr.warning('', 'Select a statement type first');
      return;
    }
    setShowConfirmCreate(true);
  };

  const handleAiJobCreated = (fileName) => {
    setAiProcessingFileName(fileName);
    setShowAiProcessingModal(true);
  };

  const handleNavigateToAdvanced = () => {
    setShowAiProcessingModal(false);
    setUploadedStatement(null);
    setUploadType('');
    dispatch(push('/whs-management'));
  };

  const handleCloseAiModal = () => {
    setShowAiProcessingModal(false);
    setUploadedStatement(null);
    setUploadType('');
  };

  const submitUploadedStatement = () => {
    if (!uploadType || !uploadedStatement) {
      toastr.warning('', 'Select type and upload a statement file first');
      return;
    }
    const data = {
      formId: 0,
      noImyForm: true,
      whdStatementType: uploadType,
      whdStatement: uploadedStatement,
      statementFormat: uploadedStatement.statementFormat
    };

    const isNonStandard = uploadedStatement.statementFormat === 'nonstandard';
    dispatch(addWhdStatement(accountId, data, isNonStandard ? null : reloadAccountFunction))
      .then((success) => {
        if (success && isNonStandard) {
          handleAiJobCreated(uploadedStatement.file?.fileName || 'Withholding Statement');
        }
      });
  };

  const navigateToDigitalStatement = () => {
    dispatch(push(`/whd-statement/${ accountId }`));
  };

  const fmtDate = d => (d ? moment(d).format('MMM DD, YYYY') : valueOrEmpty(d));

  return (
    <div className="panel" id="account-page-account-whd-statement-panel">
      <div className="panel-body">
        <div className="panel-body-caption">
          <h2 id="account-page-account-whd-statement-name"><strong>Account Withholding Statement:</strong></h2>
        </div>

        { withholdingStatement && canView && <div className="mb-3 card card-info p-2">
          <div><b>Name:</b> { withholdingStatement.statementName || 'N/A' } (Draft)</div>
          <div><b>Effective:</b> { fmtDate(withholdingStatement.effectiveDate) } { withholdingStatement.effectiveTo && `- ${ fmtDate(withholdingStatement.effectiveTo) }` }</div>
          <div><b>Validity:</b> { whdValidationResult?.valid ? <span className="valid-text">Valid</span> : <span className="invalid-text">Invalid</span> }</div>
          <div className="mt-2">
            <button type="button" className="btn btn-primary btn-sm" onClick={ navigateToDigitalStatement }>Open Draft</button>
          </div>
        </div> }

        { !withholdingStatement && <div className="account-whd-columns">
          <div className="whd-col col-6">
            <h3>Create An Empty Withholding Statement</h3>
            { canCreate && <div className="upload-row">
              <div className="upload-input">
                <div className="form-group">
                  <div className="col-6 col-sm-12 pr-0">
                    <div className="form-group">
                      <div className="col-8 col-sm-12 pr-2">
                        <label htmlFor="account-whd-create-type" className="form-label"> </label>
                      </div>
                      <div className="col-8 col-sm-12 pr-2">
                        <div>
                          <select
                            id="account-whd-create-type"
                            className="form-select"
                            value={ createType }
                            onChange={ onCreateTypeChange }
                            disabled={ isCreatingNewWhd || isAddingStatement }
                          >
                            <option value="" disabled>Choose a statement type</option>
                            <option value="altwhdstatement">Alt Statement</option>
                            <option value="detailedwhdstatement">Detailed Statement</option>
                            <option value="simplewhdstatement">Simple Statement</option>
                            <option value="pooledwhdstatement">Pooled Statement</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <button
                  id="account-whd-create-btn"
                  type="button"
                  className="btn btn-primary upload-btn"
                  disabled={ !createType || isCreatingNewWhd || isAddingStatement || mode !== 'Create' }
                  onClick={ openCreateConfirm }
                >Create Statement</button>
              </div>
              { showConfirmCreate && <div className="mt-2">
                <div className="toast toast-warning">
                  <span>Confirm creation of { StatementTypes.getDescription(StatementTypes.fromString(createType)) }?</span>
                  <div className="mt-2">
                    <button type="button" className="btn btn-success btn-sm mr-1" onClick={ confirmCreate }>Yes</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={ () => setShowConfirmCreate(false) }>No</button>
                  </div>
                </div>
              </div> }
            </div> }
            { !canCreate && <p>Insufficient permissions to create a new digital withholding statement.</p> }
          </div>
          {/* Upload Column */}
          <div className="whd-col col-6">
            <h3 className="whd-upload-title">Upload a Withholding Statement</h3>
            { canUpload && <div className="upload-row">
              <div className="upload-input">
                <WithholdingStatementUpload
                  statementType={ uploadType }
                  formErrors={ null }
                  onStatementTypeChange={ onUploadTypeChange }
                  onStatementFormatChange={ () => {} }
                  onFileUploadSuccess={ onFileUploadSuccess }
                  onFileUploadError={ onFileUploadError }
                  onFileUploadCancel={ onFileUploadCancel }
                  onFileDeleteSuccess={ onDeleteUploaded }
                  disableConfirm={ () => {} }
                  ddLabelText=""
                  singleColumnLayout
                />
              </div>
              <div className="text-right">
                <button
                  id="account-whd-upload-btn"
                  type="button"
                  className="btn btn-primary upload-btn"
                  disabled={ !uploadType || !uploadedStatement || isAddingStatement || mode !== 'Add' }
                  onClick={ submitUploadedStatement }
                >Upload Statement</button>
              </div>
            </div> }
            { !canUpload && <p>Insufficient permissions to upload a withholding statement.</p> }
          </div>
        </div> }
      </div>
      <WhdAiProcessingModal
        isOpen={ showAiProcessingModal }
        onClose={ handleCloseAiModal }
        onNavigateToAdvanced={ handleNavigateToAdvanced }
        fileName={ aiProcessingFileName }
      />
    </div>
  );
};

AccountWhdStatementPanel.propTypes = {
  accountId: PropTypes.number.isRequired,
  reloadAccountFunction: PropTypes.func.isRequired
};

export default AccountWhdStatementPanel;
